/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — auth-firm-roles.ts
   Role-based access control for firm/firm+ plans.

   Roles:
   - owner: Created the firm subscription. Full control. Can't be removed
            or demoted. There is exactly one owner per firm.
   - admin: Can add/remove non-owner members and change their roles.
            Cannot remove the owner or demote the owner.
   - member: Standard user. Can use NoteFlow but can't manage the team.

   Permission summary:
   ┌─────────────────┬───────┬───────┬────────┐
   │ Action          │ owner │ admin │ member │
   ├─────────────────┼───────┼───────┼────────┤
   │ View team       │  ✓   │  ✓   │   ✓   │
   │ Add member      │  ✓   │  ✓   │       │
   │ Remove member   │  ✓   │  ✓*  │       │
   │ Change to admin │  ✓   │  ✓   │       │
   │ Remove owner    │       │       │       │
   │ Cancel subscr.  │  ✓   │       │       │
   └─────────────────┴───────┴───────┴────────┘
   * admin cannot remove owner
   ═══════════════════════════════════════════════════════════════════════ */

export type FirmRole = 'owner' | 'admin' | 'member';

export interface FirmMember {
  email: string;
  name?: string;
  role: FirmRole;
  status?: 'pending' | 'active'; // Phase 3
  addedAt: string;
  addedBy?: string; // email of who added them
}

export interface FirmData {
  members: FirmMember[];
  memberEmails: string[];   // denormalized for Firestore array-contains queries
  maxSeats: number;
  ownerEmail?: string;       // canonical owner email (matches subscription holder)
}

// ─── Role Check Helpers ───────────────────────────────────────────────────

/** Returns the role of a given email within the firm, or null if not a member. */
export function getRoleForEmail(firm: FirmData | null, email: string | null | undefined): FirmRole | null {
  if (!firm || !email) return null;
  const lower = email.toLowerCase();
  if (firm.ownerEmail && firm.ownerEmail.toLowerCase() === lower) return 'owner';
  const member = (firm.members || []).find(m => m.email.toLowerCase() === lower);
  return member ? member.role : null;
}

/** True if the email is the firm owner. */
export function isFirmOwner(firm: FirmData | null, email: string | null | undefined): boolean {
  return getRoleForEmail(firm, email) === 'owner';
}

/** True if the email is owner or admin. */
export function isFirmAdmin(firm: FirmData | null, email: string | null | undefined): boolean {
  const role = getRoleForEmail(firm, email);
  return role === 'owner' || role === 'admin';
}

/** True if the email can manage team members (add/remove/change roles). */
export function canManageTeam(firm: FirmData | null, email: string | null | undefined): boolean {
  return isFirmAdmin(firm, email);
}

/** True if `actor` can change `target`'s role to `newRole`. */
export function canChangeRole(
  firm: FirmData | null,
  actorEmail: string | null | undefined,
  targetEmail: string,
  newRole: FirmRole,
): { allowed: boolean; reason?: string } {
  if (!firm) return { allowed: false, reason: 'No firm data.' };
  if (!actorEmail) return { allowed: false, reason: 'Not signed in.' };

  const actorRole = getRoleForEmail(firm, actorEmail);
  const targetRole = getRoleForEmail(firm, targetEmail);

  if (!actorRole) return { allowed: false, reason: 'You are not a member of this firm.' };
  if (!targetRole) return { allowed: false, reason: 'Target user is not a member.' };

  // Only owners and admins can change roles
  if (actorRole !== 'owner' && actorRole !== 'admin') {
    return { allowed: false, reason: 'Only owners and admins can change roles.' };
  }
  // Owner cannot be demoted (there is exactly one)
  if (targetRole === 'owner') {
    return { allowed: false, reason: 'The firm owner role cannot be changed.' };
  }
  // Cannot promote anyone to owner via this path (transferring ownership is a separate flow)
  if (newRole === 'owner') {
    return { allowed: false, reason: 'Use the transfer-ownership flow to change owner.' };
  }
  // Admins can only modify member-level changes; only owner can demote another admin
  if (actorRole === 'admin' && targetRole === 'admin') {
    return { allowed: false, reason: 'Only the owner can change another admin\'s role.' };
  }
  return { allowed: true };
}

/** True if `actor` can remove `target` from the firm. */
export function canRemoveMember(
  firm: FirmData | null,
  actorEmail: string | null | undefined,
  targetEmail: string,
): { allowed: boolean; reason?: string } {
  if (!firm) return { allowed: false, reason: 'No firm data.' };
  if (!actorEmail) return { allowed: false, reason: 'Not signed in.' };

  const actorRole = getRoleForEmail(firm, actorEmail);
  const targetRole = getRoleForEmail(firm, targetEmail);

  if (!actorRole) return { allowed: false, reason: 'You are not a member of this firm.' };
  if (!targetRole) return { allowed: false, reason: 'Target is not a member.' };

  // Self-removal: anyone can leave (except owner)
  if (actorEmail.toLowerCase() === targetEmail.toLowerCase()) {
    if (actorRole === 'owner') {
      return { allowed: false, reason: 'Owner cannot leave the firm. Transfer ownership first or cancel the subscription.' };
    }
    return { allowed: true };
  }

  // Only admins/owner can remove others
  if (actorRole !== 'owner' && actorRole !== 'admin') {
    return { allowed: false, reason: 'Only owners and admins can remove members.' };
  }
  // Owner cannot be removed
  if (targetRole === 'owner') {
    return { allowed: false, reason: 'The firm owner cannot be removed.' };
  }
  // Admins cannot remove other admins (only owner can)
  if (actorRole === 'admin' && targetRole === 'admin') {
    return { allowed: false, reason: 'Only the owner can remove another admin.' };
  }
  return { allowed: true };
}

// ─── Migration: Backfill role on legacy data ──────────────────────────────

/**
 * Normalizes a firm doc loaded from Firestore. Adds default roles to legacy
 * member entries that lack the `role` field (all default to 'member').
 * Also ensures `ownerEmail` is set.
 *
 * Mutates the firm object in place AND returns it.
 */
export function normalizeFirmData(firm: any, ownerEmail: string): FirmData {
  if (!firm) firm = {};
  if (!Array.isArray(firm.members)) firm.members = [];
  if (typeof firm.maxSeats !== 'number') firm.maxSeats = 10;
  if (!firm.ownerEmail) firm.ownerEmail = ownerEmail;

  // Backfill role for legacy members
  firm.members.forEach((m: any) => {
    if (!m.role) m.role = 'member';
    if (!m.status) m.status = 'active';
  });

  // Maintain the denormalized email list
  firm.memberEmails = firm.members.map((m: any) => m.email);

  return firm as FirmData;
}
